import jsPDF from 'jspdf'
import { PlusJakartaSansRegular } from '../../assets/fonts/PlusJakartaSans/PlusJakartaSans-Regular-normal'
import { PlusJakartaSansBold } from '../../assets/fonts/PlusJakartaSans/PlusJakartaSans-Bold-bold'
import React, { useCallback } from 'react'
import ZocksLogoFullBlack from '../../assets/images/ZocksLogoFullBlack.png'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useLazyGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { useLazyDownloadGlobalFileRequestQuery } from '../../store/media/file'
import { useEffectiveConfig } from '../../common/useEffectiveConfig'
import * as Sentry from '@sentry/react'

const loadImageFromUrl = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // Important for CORS
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

const contentTypeToExtension = (contentType: string): string => {
  switch (contentType) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpeg'
    case 'image/png':
      return 'png'
    case 'image/gif':
      return 'gif'
    default:
      throw new Error(`Unsupported logo type: ${contentType}`)
  }
}

export const useExportToPDF = (elementToExport: React.RefObject<HTMLElement> | null, pdfStyles: string = '', pdfName: string = 'zocks-export.pdf') => {
  const accountId = useAppSelector(selectAccountId)
  const [triggerGetAccount] = useLazyGetAccountQuery()
  const [downloadFileRequest] = useLazyDownloadGlobalFileRequestQuery()
  const { pdfComplianceMessage } = useEffectiveConfig()

  const getLogoData = useCallback(async (): Promise<{ location: string; contentType: string } | null> => {
    const { data } = await triggerGetAccount({ accountId }).unwrap()

    const fileId = data.config.assets?.logo?.logoFile.fileId

    if (!fileId) {
      return null
    }

    const {
      data: { location, fileInfo },
    } = await downloadFileRequest({ accountId, fileId }).unwrap()

    return { location, contentType: fileInfo.contentType }
  }, [triggerGetAccount, downloadFileRequest, accountId])

  const generatePDF = useCallback(async (): Promise<jsPDF | null> => {
    if (!elementToExport || !elementToExport.current) {
      return null
    }

    const logoData = await getLogoData()

    const extension = logoData?.contentType ? contentTypeToExtension(logoData.contentType) : 'png'
    let logoToUse: string | HTMLImageElement = ZocksLogoFullBlack
    if (logoData) {
      try {
        logoToUse = await loadImageFromUrl(logoData.location)
      } catch (error) {
        console.warn('Failed to load logo from URL, using fallback:', error)
      }
    }

    const doc = new jsPDF({ unit: 'px' })

    const uniqueId = `pdf-container-${Date.now()}`

    const clonedSection = elementToExport.current.cloneNode(true) as HTMLElement
    clonedSection.classList.add(uniqueId)

    doc.addFileToVFS('PlusJakartaSans-Regular.ttf', PlusJakartaSansRegular)
    doc.addFont('PlusJakartaSans-Regular.ttf', 'PlusJakartaSans', 'normal')

    doc.addFileToVFS('PlusJakartaSans-Bold.ttf', PlusJakartaSansBold)
    doc.addFont('PlusJakartaSans-Bold.ttf', 'PlusJakartaSans', 'bold')

    const style = document.createElement('style')
    style.innerHTML = `
       @font-face {
        font-family: 'PlusJakartaSans';
        src: url('data:font/truetype;charset=utf-8;base64,${PlusJakartaSansRegular}') format('truetype');
        font-weight: normal;
      }
      @font-face {
        font-family: 'PlusJakartaSans';
        src: url('data:font/truetype;charset=utf-8;base64,${PlusJakartaSansBold}') format('truetype');
        font-weight: bold;
      }

      .${uniqueId} {
        width: 76% !important;
        font-family: 'PlusJakartaSans';

        ul {
          list-style: none !important;
          padding-left: 0 !important;
          margin-bottom: 5px !important;
        }

        ul li {
          position: relative !important;
          padding-left: 20px !important;
        }

        ul li:before {
          content: "•" !important;
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          color: black !important;
        }

        /* Hide bullet for nested list items, matching the web CSS */
        .lexical-nested-listitem {
          list-style-type: none !important;
        }
        ul li.lexical-nested-listitem:before {
          content: none !important;
        }

        .bi {
          display: none !important;
        }

        .collapse {
          display: block !important;
          height: auto !important;
          opacity: 1 !important;
          visibility: visible !important;
        }

        ul + p {
          margin-top: 20px !important;
        }

        ${pdfStyles}
      }
    `

    clonedSection.prepend(style)

    const maxLogoWidth = 400
    const maxLogoHeight = 40
    let imgWidth = 104
    let imgHeight = 40

    if (logoToUse instanceof HTMLImageElement) {
      const aspectRatio = logoToUse.naturalWidth / logoToUse.naturalHeight
      // Always maximize height to maxLogoHeight, scaling up if needed
      imgHeight = maxLogoHeight
      imgWidth = imgHeight * aspectRatio
      // If width exceeds maxLogoWidth, cap width and recalculate height
      if (imgWidth > maxLogoWidth) {
        imgWidth = maxLogoWidth
        imgHeight = imgWidth / aspectRatio
      }
    }

    const pageWidth = doc.internal.pageSize.getWidth()
    const imgX = pageWidth - imgWidth - 20
    const imgY = 15

    const inputs = clonedSection.querySelectorAll('input')
    inputs.forEach((input) => {
      const span = document.createElement('span')
      span.textContent = input.value
      span.style.textAlign = 'justify'

      input.parentNode?.replaceChild(span, input)
    })

    // FLATTEN ul > li > div > p > span[data-lexical-text] to ul > li (text)
    const flattenLexicalLists = (root: HTMLElement) => {
      const lists = root.querySelectorAll('ul')
      lists.forEach((list) => {
        list.querySelectorAll('li').forEach((li) => {
          // Check for the specific structure
          const div = li.querySelector('div[data-lexical-editor]')
          if (div) {
            const span = div.querySelector('span[data-lexical-text]')
            if (span) {
              li.textContent = span.textContent || ''
            }
          }
        })
      })
    }
    flattenLexicalLists(clonedSection)

    const footerMargin = 20
    const complianceFontSize = 8
    const complianceLineHeight = 10
    const pageHeight = doc.internal.pageSize.getHeight()
    const complianceMaxWidth = pageWidth - 80

    const calculateComplianceHeight = (complianceText: string): number => {
      doc.setFont('PlusJakartaSans', 'normal')
      doc.setFontSize(complianceFontSize)
      const wrappedText = doc.splitTextToSize(complianceText, complianceMaxWidth)
      return wrappedText.length * complianceLineHeight + footerMargin
    }

    const complianceHeight = pdfComplianceMessage ? calculateComplianceHeight(pdfComplianceMessage) : 0
    const bottomMargin = 40 + complianceHeight

    const addComplianceMessage = (pdf: jsPDF, complianceText: string) => {
      pdf.setFont('PlusJakartaSans', 'normal')
      pdf.setFontSize(complianceFontSize)
      pdf.setTextColor(150)

      const wrappedText = pdf.splitTextToSize(complianceText, complianceMaxWidth)
      const textHeight = wrappedText.length * complianceLineHeight

      const y = pageHeight - footerMargin - textHeight + complianceLineHeight

      wrappedText.forEach((line: string, idx: number) => {
        pdf.text(line, pageWidth / 2, y + idx * complianceLineHeight, { align: 'center' })
      })
    }

    return new Promise<jsPDF>((resolve, reject) => {
      doc
        .html(clonedSection, {
          callback: function (pdf) {
            const totalPages = pdf.getNumberOfPages()
            Array.from({ length: totalPages }, (_, i) => i + 1).forEach((page) => {
              pdf.setPage(page)
              doc.addImage(logoToUse, extension, imgX, imgY, imgWidth, imgHeight)
              if (pdfComplianceMessage) {
                try {
                  addComplianceMessage(pdf, pdfComplianceMessage)
                } catch (error) {
                  Sentry.captureException(error)
                }
              }
            })
            resolve(pdf)
          },
          html2canvas: {
            backgroundColor: '#ffffff',
            ignoreElements: (el) => {
              return el.classList.contains('ignore-in-pdf') || ['SVG', 'BUTTON', 'svg'].includes(el.tagName)
            },
          },
          autoPaging: 'text',
          margin: [60, 40, bottomMargin, 40],
          width: 480,
          windowWidth: 960,
        })
        .catch(reject)
    })
  }, [elementToExport, pdfStyles, pdfComplianceMessage, getLogoData])

  const exportToPDF = useCallback(
    async (name?: string) => {
      try {
        const pdf = await generatePDF()
        if (pdf) {
          pdf.save(name ?? pdfName)
        }
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    [generatePDF, pdfName],
  )

  const generatePDFBlob = useCallback(async (): Promise<Blob | null> => {
    const pdf = await generatePDF()
    if (!pdf) {
      return null
    }
    return pdf.output('blob')
  }, [generatePDF])

  return {
    exportToPDF,
    generatePDFBlob,
  }
}
